import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { FactoringProvider } from 'services/factoring'

/** The only providers the box-truck drawer offers. Also the allow-list the persisted value is validated
 *  against, so the dropdown and the restored selection can never disagree. */
export const BOX_TRUCK_FACTORING_PROVIDERS = [FactoringProvider.TAFS, FactoringProvider.RTS, FactoringProvider.ECAPITAL]

const DEFAULT_PROVIDER = FactoringProvider.TAFS

const isBoxTruckProvider = (value: unknown): value is FactoringProvider => BOX_TRUCK_FACTORING_PROVIDERS.some((provider) => provider === value)

interface BoxTruckFactoringProviderStore {
    provider: FactoringProvider
    setProvider: (provider: FactoringProvider) => void
}

/**
 * The box-truck drawer's factoring provider — its OWN persisted selection, deliberately separate from the
 * app-wide `hooks/factoringProviderStore` that Load Hub, AI Hub and the search form share.
 *
 * Separate because the drawer offers only TAFS, RTS and eCapital: reading the shared store would hand
 * FactoringApprovalChip a provider outside that list (fetching Triumph while the label reads RTS, or
 * flipping into Outgo's factorability mode with no per-load flag to back it), and writing to it would
 * change Load Hub's provider from inside a box-truck drawer. Own store, same persistence guarantee —
 * the last pick survives opening another load, remounting the drawer, and a full page reload.
 */
export const useBoxTruckFactoringProviderStore = create<BoxTruckFactoringProviderStore>()(
    persist(
        (set) => ({
            provider: DEFAULT_PROVIDER,
            setProvider: (provider) => set({ provider: isBoxTruckProvider(provider) ? provider : DEFAULT_PROVIDER }),
        }),
        {
            name: 'boxTruckFactoringProvider',
            storage: createJSONStorage(() => localStorage),
            version: 1,
            // A stored value outlives the code that wrote it: a provider later dropped from the drawer (or a
            // hand-edited/corrupt entry) would otherwise rehydrate into a chip that cannot render it. Anything
            // off the allow-list degrades to TAFS instead.
            merge: (persistedState, currentState) => ({
                ...currentState,
                provider: isBoxTruckProvider((persistedState as Partial<BoxTruckFactoringProviderStore> | undefined)?.provider)
                    ? (persistedState as BoxTruckFactoringProviderStore).provider
                    : DEFAULT_PROVIDER,
            }),
        }
    )
)
